import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { ChevronRight, Loader2, Plus, Search, Store } from "lucide-react";
import freshOrderLogo from "@/assets/freshorder-logo.png";
import { isNativeApp } from "@/lib/native/platform";
import { initPushOnLaunch } from "@/lib/native/push";
import { forgetVendor, listRememberedVendors, restoreVendor, vendorIcon, type RememberedVendor } from "@/lib/native/vendorMemory";
import { ToastAction } from "@/components/ui/toast";
import { SwipeToRemove } from "@/components/swipe-to-remove";
import { AnimatePresence } from "framer-motion";
import { requireBiometricUnlock } from "@/lib/native/biometric";

// Native launch screen (route: /app) — the first thing the Capacitor shell
// shows. Deliberately multi-vendor: a neutral FreshOrder-branded entry with a
// vendor picker and a search-by-name step (Phase 3f), so an App Store reviewer
// sees a platform serving many vendors (guideline 4.3 defense), not a rebadged
// single-tenant site. Also reachable on the web for dev/screenshots.
//
// The native app offers NO demo (founder decision, 2026-09-08): app users are
// buyers of a real distributor, and a reviewer signs in with the seeded phone +
// PIN from App Review's Sign-In Information. The "Try the Demo" path below stays
// on the web only.
//
// Push permission is requested here on launch (guideline 4.2's loudest native
// cue); the token is bound to a vendor later, once a session exists.

interface VendorSearchResult {
  name: string;
  slug: string;
  logoUrl: string | null;
}

// Mirrors the server's S10 minimum — under this the endpoint answers 400 with
// an empty list, so don't bother asking.
const SEARCH_MIN_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 250;

export default function AppLaunch() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [vendors, setVendors] = useState<RememberedVendor[]>([]);
  const [vendorsLoaded, setVendorsLoaded] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [unlockingSlug, setUnlockingSlug] = useState<string | null>(null);
  const [swipedSlug, setSwipedSlug] = useState<string | null>(null);
  const [results, setResults] = useState<VendorSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchNotice, setSearchNotice] = useState<string | null>(null);
  const searchAbort = useRef<AbortController | null>(null);

  useEffect(() => {
    initPushOnLaunch();
    listRememberedVendors().then((v) => {
      setVendors(v);
      setVendorsLoaded(true);
    });
  }, []);

  // Debounced search-by-name. GET /api/vendors/search is APP-LEVEL (no org exists
  // yet to scope it to), so a plain relative fetch is the legitimate unscoped
  // exception to the getApiPath rule — the same class of exception as the
  // /org/:slug/api/organization probe in handleAddVendor below.
  useEffect(() => {
    const q = code.trim();
    searchAbort.current?.abort();
    if (q.length < SEARCH_MIN_CHARS) {
      setResults([]);
      setSearching(false);
      setSearchNotice(null);
      return;
    }
    const controller = new AbortController();
    searchAbort.current = controller;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`/api/vendors/search?q=${encodeURIComponent(q)}`, {
          signal: controller.signal,
          headers: { Accept: "application/json" },
        });
        if (controller.signal.aborted) return;
        if (res.status === 429) {
          setResults([]);
          setSearchNotice("Too many searches — wait a minute and try again.");
          return;
        }
        if (!res.ok) {
          setResults([]);
          setSearchNotice(null);
          return;
        }
        const data = (await res.json()) as { vendors?: VendorSearchResult[] };
        setResults(Array.isArray(data.vendors) ? data.vendors : []);
        setSearchNotice(null);
      } catch (err) {
        if ((err as { name?: string })?.name === "AbortError") return;
        setResults([]);
        setSearchNotice("Couldn't reach FreshOrder. Check your connection.");
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [code]);

  // Swipe a vendor left to take it off this device's list (it only forgets the
  // tile — the account, its session and its push binding are untouched; signing
  // out inside the vendor is what stops its reminders). Undo puts it back in place.
  const removeVendor = async (vendor: RememberedVendor) => {
    setSwipedSlug(null);
    setVendors((list) => list.filter((v) => v.slug !== vendor.slug));
    await forgetVendor(vendor.slug);
    toast({
      title: `Removed ${vendor.orgName}`,
      action: (
        <ToastAction
          altText={`Undo removing ${vendor.orgName}`}
          onClick={async () => {
            await restoreVendor(vendor);
            setVendors(await listRememberedVendors());
          }}
        >
          Undo
        </ToastAction>
      ),
    });
  };

  const openVendor = async (vendor: RememberedVendor) => {
    setUnlockingSlug(vendor.slug);
    try {
      // Biometric gate on re-entry (if the buyer enabled it). The vendor's own
      // PIN screen remains the credential gate if the session has expired.
      const ok = await requireBiometricUnlock(`Open ${vendor.orgName}`);
      if (!ok) return;
      setLocation(vendor.customUrl ? `/org/${vendor.slug}/order/${vendor.customUrl}` : `/org/${vendor.slug}`);
    } finally {
      setUnlockingSlug(null);
    }
  };

  // Fallback for buyers who were handed a vendor code ("gomes") or a link
  // ("app.freshorder.io/org/gomes/..."): both normalize to a slug and resolve
  // through the org endpoint, exactly as before search existed.
  const normalizeSlug = (raw: string): string => {
    const trimmed = raw.trim().toLowerCase();
    const urlMatch = trimmed.match(/\/org\/([a-z0-9-]+)/);
    if (urlMatch) return urlMatch[1];
    return trimmed.replace(/[^a-z0-9-]/g, "");
  };
  const looksLikeCodeOrLink = (raw: string): boolean => {
    const t = raw.trim();
    if (/\/org\//.test(t)) return true;
    // A single slug-shaped token (no spaces) — offered alongside search results so
    // "gomes" works whether it was a name guess or the literal vendor code.
    return t.length >= SEARCH_MIN_CHARS && /^[a-z0-9-]+$/i.test(t);
  };

  const openSearchResult = (vendor: VendorSearchResult) => {
    // Same handoff as the code path: /org/:slug is the vendor's phone + PIN gate.
    setLocation(`/org/${vendor.slug}`);
  };

  const handleAddVendor = async () => {
    const slug = normalizeSlug(code);
    if (!slug) return;
    setChecking(true);
    try {
      // Pre-org-context org resolution — the documented exception to the
      // "no raw fetch" rule (the path is fully qualified, not relative).
      const res = await fetch(`/org/${slug}/api/organization`, { credentials: "include" });
      if (res.ok) {
        setLocation(`/org/${slug}`);
      } else {
        toast({
          title: "Vendor not found",
          description: "Check the vendor code from your supplier and try again.",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Connection problem",
        description: "Couldn't reach FreshOrder. Check your connection and try again.",
        variant: "destructive",
      });
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-white to-green-50 flex flex-col"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      {/* Frosted status-bar backdrop: the padding above scrolls away with the
          content, so scrolled text otherwise collides with the iOS clock. Zero
          height on the web (inset resolves to 0), so web rendering is unchanged. */}
      <div
        className="fixed inset-x-0 top-0 z-50 pointer-events-none backdrop-blur-md bg-white/60"
        style={{ height: "env(safe-area-inset-top, 0px)" }}
      />
      <div className="flex-1 w-full max-w-md mx-auto px-6 pt-14 pb-8 flex flex-col">
        {/* Brand header */}
        <div className="text-center mb-10">
          <img src={freshOrderLogo} alt="FreshOrder" className="h-12 mx-auto mb-4" data-testid="launch-logo" />
          <p className="text-gray-600 text-base">
            Order from your produce &amp; food suppliers — all in one app.
          </p>
        </div>

        {/* Remembered vendors */}
        {vendorsLoaded && vendors.length > 0 && (
          <div className="mb-8" data-testid="launch-vendor-list">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
              Your vendors
            </h2>
            <div className="flex flex-col gap-3">
              <AnimatePresence initial={false}>
              {vendors.map((vendor) => (
                <SwipeToRemove
                  key={vendor.slug}
                  label={vendor.orgName || vendor.slug}
                  open={swipedSlug === vendor.slug}
                  onOpenChange={(open) => setSwipedSlug(open ? vendor.slug : null)}
                  onRemove={() => removeVendor(vendor)}
                  onTap={() => {
                    if (swipedSlug) setSwipedSlug(null);
                    else openVendor(vendor);
                  }}
                  testId={`launch-vendor-${vendor.slug}`}
                >
                <Card className="cursor-pointer hover:shadow-md transition-shadow">
                  {/* One fixed card anatomy for every vendor: 44px logo tile on the left,
                      bold company name, subtle "Tap to open" byline, chevron. min-h keeps
                      all cards the same comfortable height whether or not a logo exists.

                      The tile renders the org's SQUARE mark (favicon) via vendorIcon() —
                      the same asset the admin dashboard uses as the org icon. A wide
                      logo+wordmark lockup letterboxes to an unreadable strip in a 44px box.

                      The tile — not the <img> — carries the white ground, and it uses
                      bg-[#fff] rather than bg-white on purpose: index.css forces
                      width:100%!important on `.bg-white` under 640px, which stretched the
                      logo across the entire row and squeezed the name to nothing. Both
                      that rule and this site are now fixed; keep them that way. */}
                  <CardContent className="flex items-center gap-3 px-4 py-4 min-h-[76px]">
                    <span
                      className={`h-11 w-11 shrink-0 overflow-hidden rounded-lg flex items-center justify-center ${
                        vendorIcon(vendor) ? "bg-[#fff] border border-gray-200" : "bg-primary/10"
                      }`}
                    >
                      {vendorIcon(vendor) ? (
                        <img
                          src={vendorIcon(vendor)!}
                          alt=""
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <Store className="h-5 w-5 text-primary" />
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 truncate">{vendor.orgName || vendor.slug}</div>
                      <div className="text-sm text-gray-500 truncate">Tap to open</div>
                    </div>
                    {unlockingSlug === vendor.slug ? (
                      <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </CardContent>
                </Card>
                </SwipeToRemove>
              ))}
              </AnimatePresence>
            </div>
          </div>
        )}

        {/* Add a vendor */}
        <div className="mb-6">
          {vendorsLoaded && vendors.length === 0 && (
            <h2 className="text-lg font-semibold text-gray-900 mb-2 text-center">
              Connect your supplier
            </h2>
          )}
          {showAdd || (vendorsLoaded && vendors.length === 0) ? (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Search your supplier by name"
                  autoCapitalize="none"
                  autoCorrect="off"
                  className="pl-9 pr-9"
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    if (results.length === 1) openSearchResult(results[0]);
                    else if (looksLikeCodeOrLink(code)) handleAddVendor();
                  }}
                  data-testid="launch-vendor-search"
                />
                {searching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                )}
              </div>

              {/* Search results: name + logo → tap → the /org/:slug PIN gate. */}
              {results.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-[#fff] divide-y divide-gray-100 overflow-hidden" data-testid="launch-search-results">
                  {results.map((vendor) => (
                    <button
                      type="button"
                      key={vendor.slug}
                      onClick={() => openSearchResult(vendor)}
                      className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50 active:bg-gray-100"
                      data-testid={`launch-search-result-${vendor.slug}`}
                    >
                      <span
                        className={`h-9 w-9 shrink-0 overflow-hidden rounded-md flex items-center justify-center ${
                          vendor.logoUrl ? "bg-[#fff] border border-gray-200" : "bg-primary/10"
                        }`}
                      >
                        {vendor.logoUrl ? (
                          <img src={vendor.logoUrl} alt="" className="max-h-full max-w-full object-contain" />
                        ) : (
                          <Store className="h-4 w-4 text-primary" />
                        )}
                      </span>
                      <span className="flex-1 min-w-0 font-medium text-gray-900 truncate">{vendor.name}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                    </button>
                  ))}
                </div>
              )}

              {searchNotice && (
                <p className="text-xs text-amber-700 text-center" data-testid="launch-search-notice">{searchNotice}</p>
              )}
              {!searching && !searchNotice && code.trim().length >= SEARCH_MIN_CHARS && results.length === 0 && (
                <p className="text-xs text-gray-500 text-center" data-testid="launch-search-empty">
                  No supplier found by that name. Ask your supplier for their FreshOrder link or code.
                </p>
              )}

              {/* Code / link fallback — the pre-search entry path, kept for buyers
                  who were handed a slug or URL by their supplier. */}
              {looksLikeCodeOrLink(code) && (
                <Button
                  variant={results.length > 0 ? "outline" : "default"}
                  className="w-full"
                  onClick={handleAddVendor}
                  disabled={checking}
                  data-testid="launch-vendor-continue"
                >
                  {checking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Continue with vendor code “{normalizeSlug(code)}”
                </Button>
              )}
              <p className="text-xs text-gray-500 text-center">
                Type your supplier's name, or paste the link or code they gave you.
              </p>
            </div>
          ) : (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowAdd(true)}
              data-testid="launch-add-vendor"
            >
              <Plus className="h-4 w-4 mr-2" /> Add a vendor
            </Button>
          )}
        </div>

        <div className="flex-1" />

        {/* Demo entry — WEB ONLY (founder decision, 2026-09-08: the native app offers no
            demo; app users are buyers of a real distributor, and the App Store reviewer
            signs in with the seeded phone + PIN). On the web it is still shown only while
            the browser has no remembered vendors (2026-09-03 rationale: anyone with a
            vendor was onboarded by a supplier who already bought FreshOrder, so a
            persistent demo button is noise). */}
        {!isNativeApp() && vendorsLoaded && vendors.length === 0 && (
          <div className="text-center space-y-3 pb-2">
            <div className="flex items-center gap-3 text-gray-400 text-xs uppercase tracking-wide">
              <div className="flex-1 border-t border-gray-200" />
              New to FreshOrder?
              <div className="flex-1 border-t border-gray-200" />
            </div>
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => setLocation("/demo")}
              data-testid="launch-try-demo"
            >
              Try the Demo
            </Button>
            <p className="text-xs text-gray-400">
              Explore a sample vendor — no account needed.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
